import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Crown, Check, Sparkles, Loader2, BadgeCheck, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";

/**
 * BrandSubscriptionCard
 * - Shows current Brand Pro subscription status.
 * - Free brand: shows monthly usage + benefits + "Upgrade to Pro ₹999/year" button.
 * - Pro brand: shows active status, expiry, and days remaining.
 *
 * onActivated (optional): callback fired after successful activation.
 */
export default function BrandSubscriptionCard({ onActivated }) {
  const { getAuthHeaders } = useAuth();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  const fetchSubscription = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/brand/subscription`, { headers: getAuthHeaders() });
      setSub(res.data);
    } catch (e) {
      console.error("Fetch brand subscription failed", e);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      if (typeof window !== "undefined" && window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  const handleUpgrade = async () => {
    if (processing) return;
    setProcessing(true);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) {
        toast.error("Failed to load payment gateway. Please try again.");
        setProcessing(false);
        return;
      }

      // Create order
      const orderRes = await axios.post(
        `${API}/brand/subscription/create-order`,
        {},
        { headers: getAuthHeaders() }
      );
      const { order_id, amount, currency, key_id } = orderRes.data;

      const options = {
        key: key_id,
        amount,
        currency,
        name: "FoundStar",
        description: "Brand Pro — ₹999 / year",
        order_id,
        theme: { color: "#7B2FBE" },
        handler: async (response) => {
          try {
            const verifyRes = await axios.post(
              `${API}/brand/subscription/verify-payment`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              },
              { headers: getAuthHeaders() }
            );
            if (verifyRes.data?.success) {
              toast.success("🎉 Pro Brand activated!");
              setSub(verifyRes.data);
              if (onActivated) onActivated(verifyRes.data);
              fetchSubscription();
            } else {
              toast.error("Activation failed. Contact support.");
            }
          } catch (err) {
            console.error("Verify error", err);
            toast.error("Payment verification failed.");
          } finally {
            setProcessing(false);
          }
        },
        modal: {
          ondismiss: () => {
            setProcessing(false);
          },
        },
        prefill: {},
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        console.error("Payment failed", resp);
        toast.error("Payment failed. Please try again.");
        setProcessing(false);
      });
      rzp.open();
    } catch (err) {
      console.error("Upgrade error", err);
      const msg = err?.response?.data?.detail || "Failed to start payment";
      toast.error(typeof msg === "string" ? msg : "Failed to start payment");
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm flex items-center justify-center min-h-[140px]">
        <Loader2 className="w-6 h-6 animate-spin text-[#7B2FBE]" />
      </div>
    );
  }

  if (!sub) return null;

  const benefits = sub.benefits || [];
  const used = sub.monthly_collab_used || 0;
  const limit = sub.monthly_collab_limit || 3;
  const usagePct = Math.min(100, Math.round((used / Math.max(1, limit)) * 100));
  const atLimit = !sub.is_pro && used >= limit;

  // --- Pro Active ---
  if (sub.is_pro) {
    const expiry = sub.expires_at ? new Date(sub.expires_at) : null;
    const expiryStr = expiry
      ? expiry.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
      : "—";
    return (
      <div
        className="rounded-2xl p-6 mb-6 shadow-md text-white relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #7B2FBE 0%, #FF69B4 100%)" }}
        data-testid="brand-subscription-card"
      >
        <div className="absolute -top-6 -right-6 opacity-20">
          <Crown className="w-28 h-28" />
        </div>
        <div className="flex items-start gap-3 relative">
          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center shrink-0">
            <Crown className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-outfit font-bold text-xl">Pro Brand Active</h3>
              <span className="bg-yellow-400 text-black text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                <BadgeCheck className="w-3 h-3" /> PRO
              </span>
            </div>
            <p className="text-white/85 text-sm mt-0.5">
              Unlimited collabs · Priority support · Featured placement
            </p>
            <div className="mt-4 bg-white/15 backdrop-blur rounded-xl p-3 flex items-center justify-between">
              <div>
                <p className="text-white/70 text-[10px] uppercase tracking-wide">Valid until</p>
                <p className="font-semibold text-sm">{expiryStr}</p>
              </div>
              <div className="text-right">
                <p className="text-white/70 text-[10px] uppercase tracking-wide">Days left</p>
                <p className="font-bold text-lg">{sub.days_remaining || 0}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Free Brand ---
  return (
    <div
      className="rounded-2xl p-6 mb-6 shadow-md border-2 border-[#7B2FBE]/10 bg-white"
      data-testid="brand-subscription-card"
    >
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
          style={{ background: "linear-gradient(135deg, #7B2FBE 0%, #FF69B4 100%)" }}>
          <Crown className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-outfit font-bold text-lg text-[#1E293B]">Upgrade to Pro Brand</h3>
            <span className="bg-gradient-to-r from-[#7B2FBE] to-[#FF69B4] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
              ₹999 / year
            </span>
          </div>
          <p className="text-[#64748B] text-sm mt-0.5">
            Free brands get <strong>{limit} collab requests / month</strong>. Go unlimited with Pro.
          </p>

          {/* Usage bar */}
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-[#64748B]">This month's collabs</span>
              <span className={`font-semibold ${atLimit ? "text-red-600" : "text-[#1E293B]"}`}>
                {used} / {limit}
              </span>
            </div>
            <div className="w-full bg-[#F1F5F9] h-2 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  atLimit ? "bg-red-500" : "bg-gradient-to-r from-[#7B2FBE] to-[#FF69B4]"
                }`}
                style={{ width: `${usagePct}%` }}
              />
            </div>
            {atLimit && (
              <p className="text-[11px] text-red-600 mt-1 font-medium">
                You've reached this month's free limit — upgrade to keep reaching out.
              </p>
            )}
          </div>

          {/* Benefits */}
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-2">
            {benefits.map((b, i) => (
              <div key={i} className="flex items-start gap-2">
                <div className="w-4 h-4 rounded-full bg-[#7B2FBE]/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-[#7B2FBE]" />
                </div>
                <span className="text-[13px] text-[#334155]">{b}</span>
              </div>
            ))}
          </div>

          {/* CTA */}
          <Button
            onClick={handleUpgrade}
            disabled={processing}
            className="mt-5 w-full sm:w-auto bg-gradient-to-r from-[#7B2FBE] to-[#FF69B4] hover:opacity-90 text-white rounded-full font-semibold px-6"
            data-testid="brand-upgrade-btn"
          >
            {processing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing…
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" /> Upgrade to Pro — ₹999/year
              </>
            )}
          </Button>
          <p className="text-[10px] text-[#94A3B8] mt-2">
            One-time yearly payment · No auto-renew · Secure via Razorpay
          </p>
        </div>
      </div>
    </div>
  );
}

// Lightweight inline upgrade prompt modal (used when brand hits limit while trying to collab).
export function ProUpgradePrompt({ open, onClose, onUpgrade, used = 3, limit = 3 }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl max-w-sm w-full shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="p-6 text-white text-center relative"
          style={{ background: "linear-gradient(135deg, #7B2FBE 0%, #FF69B4 100%)" }}
        >
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
            <Crown className="w-8 h-8" />
          </div>
          <h2 className="font-outfit font-bold text-xl">Upgrade to Pro Brand</h2>
          <p className="text-white/90 text-sm mt-1">
            You've used <strong>{used}/{limit}</strong> free collab requests this month.
          </p>
        </div>
        <div className="p-6">
          <div className="space-y-2 mb-5">
            {[
              "Unlimited collaboration requests",
              "Pro Brand verified badge",
              "Priority support & approval",
              "Featured placement in discover",
            ].map((t) => (
              <div key={t} className="flex items-start gap-2">
                <Check className="w-4 h-4 text-[#7B2FBE] mt-0.5 shrink-0" />
                <span className="text-sm text-[#334155]">{t}</span>
              </div>
            ))}
          </div>
          <Button
            onClick={onUpgrade}
            className="w-full bg-gradient-to-r from-[#7B2FBE] to-[#FF69B4] hover:opacity-90 text-white rounded-full font-semibold"
            data-testid="upgrade-prompt-cta"
          >
            <Zap className="w-4 h-4 mr-1" /> Upgrade Now — ₹999/year
          </Button>
          <button
            onClick={onClose}
            className="w-full mt-2 text-sm text-[#64748B] hover:text-[#1E293B] py-2"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
